import { useState } from 'react';

const strategies = [
  {
    id: 'scalping',
    icon: 'ri-flashlight-line',
    name: 'Scalping',
    level: 'Avansat',
    timeframe: '1–15 min',
    profit: '5–20 pip/tranzacție',
    desc: 'Tranzacții foarte scurte, deschise și închise în câteva minute sau secunde. Necesită concentrare maximă și conexiune rapidă. Nu este recomandat pentru absoluti începători.',
    pros: ['Profit rapid', 'Multă experiență practică', 'Nu ții poziții peste noapte'],
    cons: ['Stres ridicat', 'Necesită timp dedicat', 'Costuri mari de spread'],
    color: '#ffb5a7',
  },
  {
    id: 'daytrading',
    icon: 'ri-sun-line',
    name: 'Day Trading',
    level: 'Intermediar',
    timeframe: '15 min – 4 ore',
    profit: '20–80 pip/zi',
    desc: 'Poziții deschise și închise în aceeași zi. Nu există risc de gap overnight. Poți analiza piața dimineața și tranzacționa în orele active.',
    pros: ['Fără risc overnight', 'Rutină zilnică clară', 'Bun pentru a doua sursă de venit'],
    cons: ['Necesită 2–4 ore/zi', 'Decizii sub presiune', 'Emoții intense'],
    color: '#4ecdc4',
  },
  {
    id: 'swing',
    icon: 'ri-line-chart-line',
    name: 'Swing Trading',
    level: 'Recomandat Începători',
    timeframe: '4 ore – Zilnic',
    profit: '50–300 pip/tranzacție',
    desc: 'Poziții menținute câteva zile sau săptămâni, capturând "swing-urile" (oscilațiile) pieței. Cel mai potrivit pentru cei care nu pot monitoriza piața constant.',
    pros: ['Timp redus necesar', 'Stres mai mic', 'Profituri mai mari/tranzacție'],
    cons: ['Risc overnight', 'Necesită răbdare', 'Câteva tranzacții pe lună'],
    color: '#4ecdc4',
  },
  {
    id: 'position',
    icon: 'ri-calendar-check-line',
    name: 'Position Trading',
    level: 'Investitor pe Termen Lung',
    timeframe: 'Săptămânal – Lunar',
    profit: '200–1000+ pip/tranzacție',
    desc: 'Tranzacții menținute săptămâni sau luni, bazate pe analiză fundamentală și macro-economică. Cel mai puțin solicitant în termeni de timp zilnic.',
    pros: ['Stres minim', 'Puțin timp necesar', 'Profituri mari potențiale'],
    cons: ['Necesită capital mai mare', 'Răbdare de luni', 'Swap costs (overnight)'],
    color: '#0f1419',
  },
];

export default function Strategii() {
  const [active, setActive] = useState('swing');
  const current = strategies.find((s) => s.id === active) || strategies[0];

  return (
    <section id="strategii" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block text-sm font-semibold uppercase tracking-widest mb-4 px-4 py-1 rounded-full"
            style={{ background: 'rgba(78,205,196,0.12)', color: '#4ecdc4' }}
          >
            04 — Strategii pentru Începători
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif', color: '#0f1419' }}
          >
            Dezvoltă-ți Strategia
            <br />
            de Trading Câștigătoare
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            De la scalping la swing trading — descoperă ce stil ți se potrivește
          </p>
        </div>

        {/* Strategy selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {strategies.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                active === s.id ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
              style={active === s.id ? { background: '#0f1419' } : {}}
            >
              <i className={`${s.icon} text-base`} style={{ color: active === s.id ? '#4ecdc4' : '#9ca3af' }}></i>
              {s.name}
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main card */}
          <div className="lg:col-span-2 rounded-3xl overflow-hidden" style={{ background: '#f5f1e8' }}>
            <div className="p-8">
              {/* Tags row */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span
                  className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white"
                  style={{ background: '#4ecdc4' }}
                >
                  {current.level}
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <i className="ri-time-line"></i>
                  {current.timeframe}
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <i className="ri-money-dollar-circle-line"></i>
                  {current.profit}
                </span>
              </div>

              <h3
                className="text-3xl font-bold mb-4"
                style={{ fontFamily: 'Playfair Display, serif', color: '#0f1419' }}
              >
                {current.name}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8 text-base">{current.desc}</p>

              {/* Pros & Cons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-7 h-7 flex items-center justify-center rounded-full" style={{ background: 'rgba(78,205,196,0.2)' }}>
                      <i className="ri-thumb-up-line text-base" style={{ color: '#4ecdc4' }}></i>
                    </span>
                    Avantaje
                  </p>
                  <ul className="space-y-2">
                    {current.pros.map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-check-line mt-0.5 flex-shrink-0" style={{ color: '#4ecdc4' }}></i>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-red-50">
                      <i className="ri-information-line text-xs text-red-400"></i>
                    </span>
                    De Ținut Minte
                  </p>
                  <ul className="space-y-2">
                    {current.cons.map((c, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <i className="ri-alert-line mt-0.5 flex-shrink-0 text-orange-400"></i>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom image */}
            <div className="h-52 overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/9d723db543d023b17f04713eed8949db.jpg"
                alt="Trading Strategy Chart"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Side info */}
          <div className="flex flex-col gap-6">
            {/* Tip card */}
            <div
              className="rounded-3xl p-6"
              style={{ background: '#0f1419' }}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-4" style={{ background: 'rgba(78,205,196,0.15)' }}>
                <i className="ri-lightbulb-line" style={{ color: '#4ecdc4' }}></i>
              </div>
              <h4 className="text-white font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Sfatul Expertului
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Începe cu <strong className="text-white">Swing Trading</strong> — oferă timp să analizezi, nu necesită monitorizare constantă și permite să înveți fără stres excesiv.
              </p>
            </div>

            {/* Rule card */}
            <div className="rounded-3xl p-6 border-2" style={{ borderColor: '#4ecdc4', background: 'rgba(78,205,196,0.04)' }}>
              <h4 className="font-bold mb-4" style={{ color: '#0f1419', fontFamily: 'Playfair Display, serif' }}>
                Regulile de Aur
              </h4>
              <ul className="space-y-3">
                {[
                  'Urmează întotdeauna trendul',
                  'Stabilește stop-loss înainte de intrare',
                  'Risk/Reward minim 1:2',
                  'Nu tranzacționa pe emoții',
                  'Jurnalizează fiecare tranzacție',
                ].map((rule, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="font-bold text-xs mt-0.5 flex-shrink-0" style={{ color: '#4ecdc4' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>

            {/* Stats mini */}
            <div className="rounded-3xl p-6" style={{ background: '#f5f1e8' }}>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-3">Statistici Reale</p>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Traderi profitabili</span>
                    <span className="font-bold" style={{ color: '#0f1419' }}>~25%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: '25%', background: '#4ecdc4' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Au plan de trading</span>
                    <span className="font-bold" style={{ color: '#0f1419' }}>70%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: '70%', background: '#4ecdc4' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Folosesc stop-loss</span>
                    <span className="font-bold" style={{ color: '#0f1419' }}>85%</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: '85%', background: '#4ecdc4' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
